import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { api, errorMessage } from "@/lib/api";
import { plural } from "@/lib/format";

export function AddToListDialog({ open, onOpenChange, personIds, onDone }) {
  const [mode, setMode] = useState("existing");
  const [listId, setListId] = useState("");
  const [name, setName] = useState("");
  const [shared, setShared] = useState(true);
  const [saving, setSaving] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data: lists = [] } = useQuery({
    queryKey: ["lists", "active-all"],
    queryFn: async () => (await api.get("/lists?status=active&page_size=100")).data.items,
    enabled: open,
  });
  const editable = lists.filter((l) => l.can_edit);

  const submit = async () => {
    setSaving(true);
    try {
      let target;
      if (mode === "new") {
        if (!name.trim()) throw new Error("Donnez un nom à la liste.");
        const { data } = await api.post("/lists", { name, shared, person_ids: personIds });
        target = data;
        toast.success(`Liste « ${data.name} » créée`, { description: plural(data.members_count, "contact ajouté", "contacts ajoutés") });
      } else {
        if (!listId) throw new Error("Choisissez une liste.");
        const { data } = await api.post(`/lists/${listId}/members`, { person_ids: personIds, action: "add" });
        target = editable.find((l) => l.id === listId);
        toast.success(`${plural(data.changed, "contact ajouté", "contacts ajoutés")} à « ${target?.name} »`,
          { description: data.changed < personIds.length ? "Les contacts déjà présents ont été ignorés." : undefined });
      }
      queryClient.invalidateQueries({ queryKey: ["lists"] });
      queryClient.invalidateQueries({ queryKey: ["list"] });
      onOpenChange(false);
      onDone?.();
      setName("");
      setListId("");
      toast.message("Voir la liste ?", { action: { label: "Ouvrir", onClick: () => navigate(`/listes/${target.id}`) } });
    } catch (error) {
      toast.error(error.response ? errorMessage(error) : error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-to-list-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Ajouter à une liste</DialogTitle>
          <DialogDescription>{plural(personIds.length, "contact sélectionné", "contacts sélectionnés")}</DialogDescription>
        </DialogHeader>
        <Tabs value={mode} onValueChange={setMode}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="existing" data-testid="add-to-list-tab-existing">Liste existante</TabsTrigger>
            <TabsTrigger value="new" data-testid="add-to-list-tab-new">Nouvelle liste</TabsTrigger>
          </TabsList>
          <TabsContent value="existing" className="pt-3">
            {editable.length ? (
              <Select value={listId} onValueChange={setListId}>
                <SelectTrigger data-testid="add-to-list-select"><SelectValue placeholder="Choisir une liste" /></SelectTrigger>
                <SelectContent>
                  {editable.map((l) => (
                    <SelectItem key={l.id} value={l.id} data-testid={`add-to-list-option-${l.id}`}>{l.name} ({l.members_count})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-sm text-muted-foreground">Aucune liste modifiable pour l'instant : créez-en une.</p>
            )}
          </TabsContent>
          <TabsContent value="new" className="space-y-4 pt-3">
            <div className="space-y-1.5">
              <Label htmlFor="new-list-name">Nom de la liste</Label>
              <Input id="new-list-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="ex : Invitation RSSI novembre" data-testid="add-to-list-new-name-input" />
            </div>
            <label className="flex items-center justify-between gap-3 rounded-xl border border-border p-3 text-sm">
              <span><b>Partagée avec l'équipe</b><br /><span className="text-muted-foreground">Les autres membres peuvent la voir et la compléter.</span></span>
              <Switch checked={shared} onCheckedChange={setShared} data-testid="add-to-list-shared-switch" />
            </label>
          </TabsContent>
        </Tabs>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={submit} disabled={saving} data-testid="add-to-list-dialog-submit" className="rounded-full">
            {saving ? "Enregistrement…" : mode === "new" ? "Créer et ajouter" : "Ajouter"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
